import { useState, ChangeEvent, useContext, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, InfoOutlined, CheckCircleOutline, Inventory } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { exportFile } from '../../../utils';
import useRecipeV2Api from '../hooks/useRecipeV2Api';

type ValidationErrorItem = {
  rowNumber?: number;
  recipeSku?: string;
  ingredientSku?: string;
  weight?: number;
  quantity?: number;
};

type ValidationErrors = {
  ProductNotFound: ValidationErrorItem[];
  MissingWeightAndQuantity: ValidationErrorItem[];
  ProductHasNoWeightButIngredientHasWeight: ValidationErrorItem[];
};

const VALIDATION_TABS = [
  'ProductNotFound',
  'MissingWeightAndQuantity',
  'ProductHasNoWeightButIngredientHasWeight',
] as const;

const ERRORS_PAGE_SIZE = 5;

interface BulkManageDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkManageDialog = ({ open, onClose, onSuccess }: BulkManageDialogProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { selectedStore } = useContext(UserContext);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tabPages, setTabPages] = useState<Record<number, number>>({ 0: 0, 1: 0, 2: 0 });
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setErrorMessage(null);
      setSuccessMessage(null);
      setSelectedFile(null);
      setValidationErrors(null);
      setActiveTab(0);
      setTabPages({ 0: 0, 1: 0, 2: 0 });
    }
  }, [open]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(null);
    setValidationErrors(null);
    const file = event.target.files?.[0] ?? null;

    if (file) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext !== 'xlsx') {
        setErrorMessage(t('bulkManage.errorInvalidFile'));
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setSuccessMessage(t('bulkManage.fileSelected', { name: file.name }));
    }
  };

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      const blob = await api.exportRecipes([], 'xlsx');
      exportFile(
        blob,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        `Recipes-Bulk-${selectedStore?.franchiseName ?? 'export'}.xlsx`,
      );
      onClose();
    } catch {
      setErrorMessage(t('bulkManage.errorExporting'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage(t('bulkManage.errorInvalidFile'));
      return;
    }
    setIsSaving(true);
    setErrorMessage(null);
    setValidationErrors(null);

    try {
      // In Phase 1 demo, simulate upload via MSW
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await fetch('/api/v2/recipes/bulk-import', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        if (data && (data.ProductNotFound || data.MissingWeightAndQuantity || data.ProductHasNoWeightButIngredientHasWeight)) {
          setValidationErrors(data as ValidationErrors);
          setActiveTab(0);
          setTabPages({ 0: 0, 1: 0, 2: 0 });
        } else {
          setErrorMessage(t('bulkManage.errorUploading'));
        }
      } else {
        setShowUploadSuccess(true);
        setSelectedFile(null);
        setSuccessMessage(null);
        onSuccess();
        onClose();
      }
    } catch {
      setErrorMessage(t('bulkManage.errorUploading'));
    } finally {
      setIsSaving(false);
    }
  };

  const getTabItems = (tabIndex: number): ValidationErrorItem[] => {
    if (!validationErrors) return [];
    return validationErrors[VALIDATION_TABS[tabIndex]] ?? [];
  };

  const getPagedItems = (tabIndex: number): ValidationErrorItem[] => {
    const items = getTabItems(tabIndex);
    const page = tabPages[tabIndex] ?? 0;
    return items.slice(page * ERRORS_PAGE_SIZE, (page + 1) * ERRORS_PAGE_SIZE);
  };

  return (
    <>
      <Dialog open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Inventory sx={{ mr: 1, fontSize: 20 }} />
          <Typography variant="textXL"
            sx={{ flex: 1 }}>
            {t('bulkManage.title')}
          </Typography>
          <IconButton onClick={onClose}
            size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 3 }}>
            {t('bulkManage.description')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="secondary"
              onClick={handleDownload}
              disabled={isExporting}>
              {isExporting ? t('bulkManage.exporting') : t('bulkManage.download')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => document.getElementById('bulk-file-input')?.click()}>
              {t('bulkManage.upload')}
            </Button>
          </Box>

          <input
            aria-hidden
            type="file"
            id="bulk-file-input"
            accept=".xlsx"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {errorMessage && (
            <Typography color="error"
              sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}

          {isSaving && (
            <Typography sx={{ mt: 2, color: 'text.secondary' }}>
              {t('bulkManage.validating')}
            </Typography>
          )}

          {successMessage && !isSaving && !validationErrors && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutline sx={{ color: 'success.main' }} />
              <Typography color="success.main">{successMessage}</Typography>
            </Box>
          )}

          {/* Validation errors */}
          {validationErrors && (
            <Box sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography sx={{ px: 2, pt: 2, fontWeight: 600 }}>
                {t('bulkManage.validationTitle')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{ flex: 1 }}>
                  {VALIDATION_TABS.map((key) => (
                    <Tab
                      key={key}
                      label={`${t(`bulkManage.${key}`)} (${(validationErrors[key] ?? []).length})`}
                      sx={{ fontSize: '0.78rem', textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
                <Tooltip title={t('bulkManage.errorHelp')}>
                  <IconButton size="small">
                    <InfoOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {VALIDATION_TABS.map((key, tabIndex) => (
                <Box
                  key={key}
                  hidden={activeTab !== tabIndex}
                  sx={{ px: 2, pb: 2 }}>
                  {activeTab === tabIndex && getTabItems(tabIndex).length > 0 && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('bulkManage.colRow')}</TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('bulkManage.colRecipeSku')}</TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('bulkManage.colIngredientSku')}</TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('bulkManage.colWeight')}</TableCell>
                            <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{t('bulkManage.colQuantity')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getPagedItems(tabIndex).map((item, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{item.rowNumber ?? '—'}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{item.recipeSku ?? '—'}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{item.ingredientSku ?? '—'}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{item.weight ?? '—'}</TableCell>
                              <TableCell sx={{ fontSize: '0.78rem' }}>{item.quantity ?? '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Save button */}
          {selectedFile && !validationErrors && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={isSaving}>
                {t('bulkManage.save')}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={showUploadSuccess}
        autoHideDuration={4000}
        onClose={() => setShowUploadSuccess(false)}>
        <Alert severity="success"
          onClose={() => setShowUploadSuccess(false)}>
          {t('bulkManage.uploadSuccess')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BulkManageDialog;
